import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Generate a 256x256 tileable value noise texture (replaces procedural hash noise)
function generateNoiseTexture(size) {
  // Seed a simple hash-based noise into a texture
  function hash(x, y) {
    let h = x * 127.1 + y * 311.7;
    h = Math.sin(h) * 43758.5453123;
    return h - Math.floor(h);
  }
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const idx = (y * size + x) * 4;
      // Two independent noise channels (yx like the article)
      data[idx]     = Math.floor(hash(x * 1.0, y * 1.0) * 255);
      data[idx + 1] = Math.floor(hash(x * 1.0 + 127.0, y * 1.0 + 311.0) * 255);
      data[idx + 2] = Math.floor(hash(x * 1.0 + 269.0, y * 1.0 + 183.0) * 255);
      data[idx + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.NearestMipmapLinearFilter;
  tex.magFilter = THREE.NearestMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

// Generate a 256x256 blue noise texture (void-and-cluster approximation)
function generateBlueNoiseTexture(size) {
  const data = new Uint8Array(size * size * 4);
  // Use interleaved gradient noise — excellent blue-noise-like spectral properties
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Interleaved gradient noise (Jimenez 2014)
      const v = (52.9829189 * ((0.06711056 * x + 0.00583715 * y) % 1.0)) % 1.0;
      const val = Math.floor(Math.abs(v) * 255);
      const idx = (y * size + x) * 4;
      data[idx] = val;
      data[idx + 1] = val;
      data[idx + 2] = val;
      data[idx + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  return tex;
}

const vertexShader = `
varying vec3 vWorldPosition;

void main() {
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos.xyz;
  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

const fragmentShader = `
uniform float uTime;
uniform vec3 uSunDir;
uniform vec3 uCameraPos;
uniform float uCoverage;
uniform float uCloudDensity;
uniform float uWindSpeed;
uniform float uCloudBase;
uniform float uCloudTop;
uniform float uCloudOpacity;
uniform sampler2D uNoise;
uniform sampler2D uBlueNoise;
uniform int uFrame;

varying vec3 vWorldPosition;

#define MAX_STEPS 64
#define MAX_STEPS_LIGHTS 6
#define ABSORPTION_COEFFICIENT 0.9
#define SCATTERING_ANISO 0.3
#define PI 3.14159265359

float BeersLaw(float dist, float absorption) {
  return exp(-dist * absorption);
}

float HenyeyGreenstein(float g, float mu) {
  float gg = g * g;
  return (1.0 / (4.0 * PI)) * ((1.0 - gg) / pow(1.0 + gg - 2.0 * g * mu, 1.5));
}

// Texture-based 3D noise (article approach)
float noise(vec3 x) {
  vec3 p = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);

  vec2 uv = (p.xy + vec2(37.0, 239.0) * p.z) + f.xy;
  vec2 tex = textureLod(uNoise, (uv + 0.5) / 256.0, 0.0).yx;

  return mix(tex.x, tex.y, f.z) * 2.0 - 1.0;
}

float fbm(vec3 p, bool lowRes) {
  vec3 q = p + uTime * uWindSpeed * 0.5 * vec3(1.0, -0.2, -1.0);
  float f = 0.0;
  float scale = 0.45;
  float factor = 2.02;

  int maxOctave = lowRes ? 3 : 6;

  for (int i = 0; i < 6; i++) {
    if (i >= maxOctave) break;
    f += scale * noise(q);
    q *= factor;
    factor += 0.21;
    scale *= 0.5;
  }

  return f;
}

// Ray-slab intersection
vec2 raySlab(vec3 ro, vec3 rd, float yMin, float yMax) {
  if (abs(rd.y) < 0.0001) {
    if (ro.y >= yMin && ro.y <= yMax) {
      return vec2(0.0, 10000.0);
    }
    return vec2(-1.0, -1.0);
  }
  float t0 = (yMin - ro.y) / rd.y;
  float t1 = (yMax - ro.y) / rd.y;
  if (t0 > t1) {
    float tmp = t0;
    t0 = t1;
    t1 = tmp;
  }
  if (t1 < 0.0) return vec2(-1.0, -1.0);
  t0 = max(t0, 0.0);
  return vec2(t0, t1);
}

float sampleDensity(vec3 p, bool lowRes) {
  float heightFraction = (p.y - uCloudBase) / (uCloudTop - uCloudBase);
  float heightGradient = smoothstep(0.0, 0.2, heightFraction) * smoothstep(1.0, 0.6, heightFraction);

  vec3 samplePos = p * 0.0008;
  float f = fbm(samplePos, lowRes);
  f = f * heightGradient;
  f = smoothstep(uCoverage, uCoverage + 0.25, f);

  return f * uCloudDensity;
}

// Light march toward sun (article pattern)
float lightmarch(vec3 position) {
  vec3 sunDirection = normalize(uSunDir);
  float totalDensity = 0.0;
  float marchSize = (uCloudTop - uCloudBase) * 0.05;

  for (int step = 0; step < MAX_STEPS_LIGHTS; step++) {
    position += sunDirection * marchSize * float(step + 1);

    if (position.y < uCloudBase || position.y > uCloudTop) continue;

    float lightSample = sampleDensity(position, true);
    totalDensity += lightSample;
  }

  float transmittance = BeersLaw(totalDensity, ABSORPTION_COEFFICIENT);
  return transmittance;
}

void main() {
  vec3 rayDir = normalize(vWorldPosition - uCameraPos);

  vec2 tSlab = raySlab(uCameraPos, rayDir, uCloudBase, uCloudTop);

  if (tSlab.x < 0.0) {
    discard;
  }

  float tStart = tSlab.x;
  float tEnd = min(tSlab.y, tSlab.x + 8000.0);
  float marchSize = (tEnd - tStart) / float(MAX_STEPS);

  // Blue noise offset (article pattern: temporal animation)
  float blueNoise = texture2D(uBlueNoise, gl_FragCoord.xy / 256.0).r;
  float offset = fract(blueNoise + float(uFrame % 32) / sqrt(0.5));

  float depth = 0.0;
  depth += marchSize * offset;

  vec3 sunDirection = normalize(uSunDir);
  float totalTransmittance = 1.0;
  float lightEnergy = 0.0;

  // Phase function computed once before the loop (article pattern)
  float phase = HenyeyGreenstein(SCATTERING_ANISO, dot(rayDir, sunDirection));

  for (int i = 0; i < MAX_STEPS; i++) {
    vec3 p = uCameraPos + rayDir * (tStart + depth);

    float density = sampleDensity(p, false);

    if (density > 0.0) {
      float lightTransmittance = lightmarch(p);
      // Article pattern: small ambient bias + density * phase
      float luminance = 0.025 + density * phase;

      totalTransmittance *= lightTransmittance;
      lightEnergy += totalTransmittance * luminance;
    }

    depth += marchSize;
  }

  float res = clamp(lightEnergy, 0.0, 1.0);

  if (res < 0.01 && totalTransmittance > 0.99) discard;

  // Sun color based on height
  float sunHeight = sunDirection.y;
  vec3 sunColor = mix(
    vec3(1.4, 0.6, 0.25),
    vec3(1.0, 0.98, 0.96),
    smoothstep(-0.05, 0.3, sunHeight)
  );

  // Bright white ambient base — clouds are always somewhat white
  vec3 ambientBase = mix(
    vec3(0.5, 0.45, 0.4),
    vec3(0.85, 0.87, 0.9),
    smoothstep(-0.05, 0.3, sunHeight)
  );

  // Blend sun-lit energy with white ambient base
  float cloudDark = 1.0 - totalTransmittance;
  vec3 cloudColor = sunColor * res + ambientBase * cloudDark;

  float alpha = max(cloudDark, res) * uCloudOpacity;

  gl_FragColor = vec4(cloudColor * uCloudOpacity, alpha);
}
`;

const Clouds = ({ sunPosition = [0, 1, 0], coverage = 0.45, cloudDensity = 0.04, windSpeed = 0.5, cloudBase = 800, cloudTop = 2000, cloudOpacity = 0.6 }) => {
  const meshRef = useRef();
  const materialRef = useRef();

  const noiseTexture = useMemo(() => generateNoiseTexture(256), []);
  const blueNoiseTexture = useMemo(() => generateBlueNoiseTexture(256), []);

  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uSunDir: { value: new THREE.Vector3(sunPosition[0], sunPosition[1], sunPosition[2]) },
    uCameraPos: { value: new THREE.Vector3() },
    uCoverage: { value: coverage },
    uCloudDensity: { value: cloudDensity },
    uWindSpeed: { value: windSpeed },
    uCloudBase: { value: cloudBase },
    uCloudTop: { value: cloudTop },
    uCloudOpacity: { value: cloudOpacity },
    uNoise: { value: noiseTexture },
    uBlueNoise: { value: blueNoiseTexture },
    uFrame: { value: 0 },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame(({ camera, clock }) => {
    if (meshRef.current) {
      meshRef.current.position.copy(camera.position);
    }
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      materialRef.current.uniforms.uCameraPos.value.copy(camera.position);
      materialRef.current.uniforms.uSunDir.value.set(sunPosition[0], sunPosition[1], sunPosition[2]);
      materialRef.current.uniforms.uCoverage.value = coverage;
      materialRef.current.uniforms.uCloudDensity.value = cloudDensity;
      materialRef.current.uniforms.uWindSpeed.value = windSpeed;
      materialRef.current.uniforms.uCloudBase.value = cloudBase;
      materialRef.current.uniforms.uCloudTop.value = cloudTop;
      materialRef.current.uniforms.uCloudOpacity.value = cloudOpacity;
      materialRef.current.uniforms.uFrame.value += 1;
    }
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[9000, 32, 16]} />
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent={true}
        depthWrite={false}
        side={THREE.BackSide}
      />
    </mesh>
  );
};

export default Clouds;
